<template>
    <div class="converter-app">
        <div class="tabs">
            <button
                    :class="{ active: activeTab === 'pdfToImage' }"
                    @click="activeTab = 'pdfToImage'"
            >
                PDF 转 图片
            </button>
            <button
                    :class="{ active: activeTab === 'imageToPdf' }"
                    @click="activeTab = 'imageToPdf'"
            >
                图片 转 PDF
            </button>
        </div>

        <div class="tab-content">
            <!-- PDF 转 图片 -->
            <div v-if="activeTab === 'pdfToImage'" class="tab-panel">
                <h2>📄 PDF 转 图片</h2>
                <div class="upload-section">
                    <label for="pdf-upload-p2i" class="file-upload-label">
                        <div class="upload-icon">📁</div>
                        <div class="upload-text">点击选择或拖拽 PDF 文件</div>
                        <div class="upload-hint">支持 .pdf 格式</div>
                        <input
                                id="pdf-upload-p2i"
                                type="file"
                                @change="handlePdfUpload"
                                accept=".pdf"
                                class="file-input"
                        />
                    </label>
                </div>

                <div v-if="pdfFileP2I" class="selected-file">
                    <span class="file-name">已选择: {{ pdfFileP2I.name }}</span>
                    <button @click="clearPdfFileP2I" class="clear-btn">清除</button>
                </div>

                <div class="options-section">
                    <label for="output-format-p2i">输出格式:</label>
                    <select id="output-format-p2i" v-model="outputFormatP2I">
                        <option value="png">PNG</option>
                        <option value="jpeg">JPEG</option>
                    </select>

                    <label for="scale-p2i">缩放比例:</label>
                    <input
                            id="scale-p2i"
                            type="range"
                            min="0.1"
                            max="3"
                            step="0.1"
                            v-model="scaleP2I"
                    />
                    <span>{{ Math.round(scaleP2I * 100) }}%</span>
                </div>

                <div class="action-section">
                    <!-- 使用动态类名控制按钮状态 -->
                    <button
                            @click="convertPdfToImages"
                            :class="getButtonClass(!pdfFileP2I || isProcessing)"
                            :disabled="!pdfFileP2I || isProcessing"
                    >
                        <span v-if="!isProcessing">🚀 开始转换</span>
                        <span v-else>⏳ 转换中...</span>
                    </button>
                </div>

                <div v-if="statusMessageP2I" :class="['status-message', { error: isErrorP2I }]">
                    {{ statusMessageP2I }}
                </div>

                <div v-if="imageUrls.length > 0" class="results-section">
                    <h3>转换结果:</h3>
                    <div class="image-preview-container">
                        <div
                                v-for="(url, index) in imageUrls"
                                :key="index"
                                class="image-preview-item"
                        >
                            <img :src="url" :alt="`Page ${index + 1}`" class="preview-image" />
                            <p>第 {{ index + 1 }} 页</p>
                            <a :href="url" :download="`page_${index + 1}.${outputFormatP2I}`" class="download-link">
                                下载
                            </a>
                        </div>
                    </div>
                    <button @click="downloadAllImages" :class="getButtonClass(false)" :disabled="false">
                        📥 全部打包下载 (ZIP)
                    </button>
                </div>
            </div>

            <!-- 图片 转 PDF -->
            <div v-if="activeTab === 'imageToPdf'" class="tab-panel">
                <h2>🖼️ 图片 转 PDF</h2>
                <div class="upload-section">
                    <label for="image-upload-i2p" class="file-upload-label multi">
                        <div class="upload-icon">📁</div>
                        <div class="upload-text">点击选择或拖拽图片文件</div>
                        <div class="upload-hint">支持 .png, .jpg, .jpeg, .gif, .webp</div>
                        <input
                                id="image-upload-i2p"
                                type="file"
                                @change="handleImageUpload"
                                accept="image/*"
                                class="file-input"
                                multiple
                        />
                    </label>
                </div>

                <div v-if="imageFilesI2P.length > 0" class="selected-files">
                    <h3>已选择的图片:</h3>
                    <ul class="file-list">
                        <li v-for="(file, index) in imageFilesI2P" :key="index" class="file-list-item">
                            <span class="file-name">{{ file.name }}</span>
                            <button @click="removeImageFile(index)" class="remove-btn">移除</button>
                        </li>
                    </ul>
                    <button @click="clearAllImageFiles" class="clear-all-btn">清空列表</button>
                </div>

                <div class="action-section">
                    <!-- 使用动态类名控制按钮状态 -->
                    <button
                            @click="convertImagesToPdf"
                            :class="getButtonClass(imageFilesI2P.length === 0 || isProcessing)"
                            :disabled="imageFilesI2P.length === 0 || isProcessing"
                    >
                        <span v-if="!isProcessing">🚀 开始转换</span>
                        <span v-else>⏳ 转换中...</span>
                    </button>
                </div>

                <div v-if="statusMessageI2P" :class="['status-message', { error: isErrorI2P }]">
                    {{ statusMessageI2P }}
                </div>

                <div v-if="pdfUrlI2P" class="results-section">
                    <h3>转换结果:</h3>
                    <div class="pdf-preview">
                        <embed :src="pdfUrlI2P" type="application/pdf" width="100%" height="400px" />
                    </div>
                    <a :href="pdfUrlI2P" download="converted_from_images.pdf" :class="getButtonClass(false)" :disabled="false">
                        💾 下载 PDF 文件
                    </a>
                </div>
            </div>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>🖼️ PDF 转图片 / 图片转 PDF 在线工具</h2>
            <p>双向转换工具：将 PDF 页面导出为高清图片，或将多张图片合并为 PDF 文档。支持 PNG、JPEG 格式，可自定义分辨率。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>双向转换</strong>：PDF 转图片 + 图片转 PDF 二合一</li>
                <li><strong>高清输出</strong>：支持 1x-3x 缩放比例，满足不同清晰度需求</li>
                <li><strong>批量处理</strong>：多页 PDF 可打包为 ZIP 下载</li>
                <li><strong>格式灵活</strong>：支持 PNG、JPEG 两种图片格式</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>选择「PDF 转图片」或「图片转 PDF」标签</li>
                <li>上传文件（支持拖拽）</li>
                <li>调整输出参数（格式、质量等）</li>
                <li>点击转换并下载结果</li>
            </ol>

            <h3>💡 适用场景</h3>
            <p>PDF 转图片：制作预览图、分享到社交媒体、插入 PPT<br>
            图片转 PDF：整理照片、制作电子相册、提交材料</p>

            <h3>🔒 隐私说明</h3>
            <p>所有转换在浏览器本地完成，文件不会上传到服务器，保护您的隐私安全。</p>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';
import { jsPDF } from 'jspdf';
    import { saveAs } from 'file-saver';
    import JSZip from 'jszip';

    export default {
        name: 'PdfImageConverter',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: 'PDF转图片/图片转PDF在线工具 - FreeToolBox',
                seoDescription: '免费在线PDF转图片工具，支持PDF转PNG/JPEG，同时支持图片转PDF。浏览器本地处理，保护隐私，无需注册。',
                seoKeywords: 'pdf转图片,pdf转png,图片转pdf,在线pdf转换,pdf工具',
                activeTab: 'pdfToImage', // 'pdfToImage', 'imageToPdf'

                // PDF to Image
                pdfFileP2I: null,
                outputFormatP2I: 'png',
                scaleP2I: 2.0,
                imageUrls: [],
                isProcessing: false,
                statusMessageP2I: '',
                isErrorP2I: false,

                // Image to PDF
                imageFilesI2P: [],
                pdfUrlI2P: null,
                statusMessageI2P: '',
                isErrorI2P: false,
            };
        },
        methods: {
            // --- 核心方法：根据禁用状态返回按钮类名 ---
            getButtonClass(isDisabled) {
                // 始终包含基础类 'btn'
                const classes = ['btn'];

                if (isDisabled) {
                    // 如果禁用，添加 'btn-disabled' 类
                    classes.push('btn-disabled');
                } else {
                    // 如果启用，添加 'btn-enabled' 类
                    classes.push('btn-enabled');
                }

                return classes.join(' '); // 返回拼接好的类名字符串
            },
            // --- Tab Management ---
            setActiveTab(tabName) {
                this.activeTab = tabName;
            },

            // --- PDF to Image Methods ---
            handlePdfUpload(event) {
                const file = event.target.files[0];
                if (file && file.type === 'application/pdf') {
                    this.pdfFileP2I = file;
                    this.clearResultsP2I();
                } else {
                    this.showStatusP2I('请选择一个有效的 PDF 文件', true);
                }
                event.target.value = '';
            },
            clearPdfFileP2I() {
                this.pdfFileP2I = null;
                this.clearResultsP2I();
            },
            clearResultsP2I() {
                this.imageUrls.forEach(url => URL.revokeObjectURL(url));
                this.imageUrls = [];
                this.statusMessageP2I = '';
                this.isErrorP2I = false;
            },
            showStatusP2I(message, isError = false) {
                this.statusMessageP2I = message;
                this.isErrorP2I = isError;
            },
            async convertPdfToImages() {
                if (!this.pdfFileP2I) return;

                this.isProcessing = true;
                this.clearResultsP2I();
                this.showStatusP2I('正在加载 PDF...');

                // 清理旧的 URLs
                this.imageUrls.forEach(url => URL.revokeObjectURL(url));
                this.imageUrls = [];

                try {
                    const pdfjsLib = await import('pdfjs-dist/build/pdf.min.mjs');
                    // 设置 worker
                    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'; // 确保 public 目录下有此文件

                    const arrayBuffer = await this.pdfFileP2I.arrayBuffer();
                    this.showStatusP2I('正在解析 PDF...');
                    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
                    const pdf = await loadingTask.promise;

                    const numPages = pdf.numPages;
                    this.showStatusP2I(`正在转换 ${numPages} 页...`);

                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
                        this.showStatusP2I(`正在转换第 ${pageNumber}/${numPages} 页...`);
                        const page = await pdf.getPage(pageNumber);

                        const viewport = page.getViewport({ scale: this.scaleP2I });
                        canvas.width = viewport.width;
                        canvas.height = viewport.height;

                        // 支持透明背景的 PNG
                        if (this.outputFormatP2I === 'png') {
                            ctx.clearRect(0, 0, canvas.width, canvas.height);
                        } else {
                            // JPEG 不支持透明度，填充白色背景
                            ctx.fillStyle = 'white';
                            ctx.fillRect(0, 0, canvas.width, canvas.height);
                        }

                        await page.render({
                            canvasContext: ctx,
                            viewport: viewport,
                        }).promise;

                        // 使用 Promise 包装 toBlob 以确保页面按顺序处理
                        await new Promise((resolve) => {
                            canvas.toBlob(
                                (blob) => {
                                    const url = URL.createObjectURL(blob);
                                    this.imageUrls.push(url);
                                    resolve();
                                },
                                this.outputFormatP2I === 'png' ? 'image/png' : 'image/jpeg',
                                this.outputFormatP2I === 'png' ? 1 : 0.92 // PNG 无损, JPEG 质量
                            );
                        });
                    }

                    this.showStatusP2I(`✅ 转换完成！共生成 ${numPages} 张图片。`, false);
                } catch (error) {
                    console.error('PDF to Image Error:', error);
                    this.showStatusP2I(`❌ 转换失败: ${error.message || '未知错误'}`, true);
                } finally {
                    this.isProcessing = false;
                }
            },
            async downloadAllImages() {
                if (this.imageUrls.length === 0) return;

                this.isProcessing = true;
                this.showStatusP2I('正在打包图片...');
                try {
                    const zip = new JSZip();
                    const imgFolder = zip.folder("pdf_images");

                    for (let i = 0; i < this.imageUrls.length; i++) {
                        const url = this.imageUrls[i];
                        const response = await fetch(url);
                        const blob = await response.blob();
                        const fileName = `page_${i + 1}.${this.outputFormatP2I}`;
                        imgFolder.file(fileName, blob);
                    }

                    const content = await zip.generateAsync({ type: "blob" });
                    saveAs(content, "pdf_pages.zip");
                    this.showStatusP2I('✅ 打包下载完成！', false);
                } catch (error) {
                    console.error('Download All Images Error:', error);
                    this.showStatusP2I(`❌ 打包失败: ${error.message || '未知错误'}`, true);
                } finally {
                    this.isProcessing = false;
                }
            },

            // --- Image to PDF Methods ---
            handleImageUpload(event) {
                const files = Array.from(event.target.files);
                const validFiles = files.filter(file => file.type.startsWith('image/'));
                if (validFiles.length !== files.length) {
                    this.showStatusI2P('部分文件不是有效图片，已忽略。', true);
                }
                this.imageFilesI2P.push(...validFiles);
                event.target.value = '';
            },
            removeImageFile(index) {
                this.imageFilesI2P.splice(index, 1);
            },
            clearAllImageFiles() {
                this.imageFilesI2P = [];
                this.clearResultsI2P();
            },
            clearResultsI2P() {
                if (this.pdfUrlI2P) {
                    URL.revokeObjectURL(this.pdfUrlI2P);
                    this.pdfUrlI2P = null;
                }
                this.statusMessageI2P = '';
                this.isErrorI2P = false;
            },
            showStatusI2P(message, isError = false) {
                this.statusMessageI2P = message;
                this.isErrorI2P = isError;
            },
            async convertImagesToPdf() {
                if (this.imageFilesI2P.length === 0) return;

                this.isProcessing = true;
                this.clearResultsI2P();
                this.showStatusI2P('正在生成 PDF...');

                try {
                    // 使用 jspdf，它会自动处理图片加载
                    const pdf = new jsPDF();

                    for (let i = 0; i < this.imageFilesI2P.length; i++) {
                        const file = this.imageFilesI2P[i];
                        this.showStatusI2P(`正在处理图片 ${i + 1}/${this.imageFilesI2P.length}...`);

                        // 等待 PDF 添加完当前图片再处理下一张
                        await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                                const imgData = e.target.result;
                                if (i > 0) {
                                    pdf.addPage(); // 从第二张图开始添加新页
                                }

                                const imgProps = pdf.getImageProperties(imgData);
                                const pdfWidth = pdf.internal.pageSize.getWidth();
                                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

                                // 添加图片到 PDF
                                pdf.addImage(
                                    imgData,
                                    imgProps.fileType.toUpperCase(), // 格式
                                    0, // x
                                    0, // y
                                    pdfWidth, // width
                                    pdfHeight, // height
                                    undefined, // alias
                                    'FAST' // compression
                                );
                                resolve();
                            };
                            reader.onerror = () => {
                                reject(new Error(`读取图片文件失败: ${file.name}`));
                            };
                            reader.readAsDataURL(file);
                        });
                    }

                    const pdfBlob = pdf.output('blob');
                    this.pdfUrlI2P = URL.createObjectURL(pdfBlob);
                    this.showStatusI2P('✅ PDF 生成完成！', false);
                } catch (error) {
                    console.error('Image to PDF Error:', error);
                    this.showStatusI2P(`❌ PDF 生成失败: ${error.message || '未知错误'}`, true);
                } finally {
                    this.isProcessing = false;
                }
            },
        },
        beforeUnmount() {
            // 清理所有创建的 Object URLs 以防内存泄漏
            this.imageUrls.forEach(url => URL.revokeObjectURL(url));
            if (this.pdfUrlI2P) URL.revokeObjectURL(this.pdfUrlI2P);
        },
    };
</script>

<style scoped>
    :root {
        --primary-color: #1e88e5;
        --primary-dark: #0d47a1;
        --secondary-color: #21cbf3;
        --success-color: #4caf50;
        --error-color: #f44336;
        --warning-color: #ff9800;
        --light-bg: #f5fbff;
        --light-border: #bbdefb;
        --dark-text: #333;
        --light-text: #666;
        --tab-bg: #e3f2fd;
        --tab-active-bg: #bbdefb;
        --button-disabled-bg: #cccccc;
        --button-disabled-text: #666666;
        --button-hover-shadow: rgba(33, 150, 243, 0.4);
        --button-primary-gradient: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
        --button-primary-gradient-hover: linear-gradient(90deg, var(--primary-dark), var(--primary-color));
        --button-secondary-bg: var(--success-color);
        --button-secondary-bg-hover: #388e3c;
    }

    * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    .converter-app {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        background: linear-gradient(135deg, #e0f7fa, #bbdefb);
        display: flex;
        flex-direction: column;
        align-items: center;
        min-height: 100vh;
        padding: 20px;
    }

    .tabs {
        display: flex;
        justify-content: center;
        margin-bottom: 20px;
        background-color: var(--tab-bg);
        border-radius: 10px;
        overflow: hidden;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    }

    .tabs button {
        flex: 1;
        padding: 15px 20px;
        border: none;
        background-color: transparent;
        cursor: pointer;
        font-size: 1em;
        font-weight: 500;
        color: var(--dark-text);
        transition: background-color 0.3s ease, color 0.3s ease;
        outline: none;
    }

    .tabs button:hover {
        background-color: var(--tab-active-bg);
    }

    .tabs button.active {
        background-color: var(--primary-color);
        color: white;
    }

    .tab-content {
        width: 100%;
        max-width: 800px;
        background-color: white;
        border-radius: 20px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        padding: 30px;
        box-sizing: border-box;
    }

    .tab-panel h2 {
        text-align: center;
        color: var(--primary-color);
        margin-bottom: 20px;
        font-size: 1.8em;
    }

    .upload-section {
        margin-bottom: 20px;
    }

    .file-upload-label {
        display: block;
        border: 3px dashed var(--primary-color);
        border-radius: 15px;
        padding: 30px 20px;
        cursor: pointer;
        transition: all 0.3s ease;
        background-color: var(--light-bg);
        position: relative;
        overflow: hidden;
        text-align: center;
    }

    .file-upload-label:hover {
        background-color: #e3f2fd;
        border-color: var(--primary-dark);
    }

    .file-upload-label.multi {
        padding: 20px 20px; /* Smaller padding for multi-select */
    }

    .file-input {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        opacity: 0;
        cursor: pointer;
    }

    .upload-icon {
        font-size: 3em;
        margin-bottom: 10px;
    }

    .upload-text {
        font-size: 1.1em;
        color: var(--dark-text);
        margin-bottom: 5px;
    }

    .upload-hint {
        font-size: 0.9em;
        color: #888;
    }

    .selected-file, .selected-files {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #e8f5e9;
        padding: 12px 15px;
        border-radius: 10px;
        margin-bottom: 20px;
        box-sizing: border-box;
        flex-wrap: wrap; /* Allow wrapping on small screens */
    }

    .selected-files {
        background-color: #fff8e1; /* Different color for image list */
        flex-direction: column;
        align-items: stretch;
    }

    .file-list {
        list-style: none;
        padding: 0;
        margin: 10px 0;
        width: 100%;
    }

    .file-list-item {
        display: flex;
        justify-content: space-between;
        padding: 5px 0;
        border-bottom: 1px solid #eee;
    }

    .file-list-item:last-child {
        border-bottom: none;
    }

    .file-name {
        font-weight: 500;
        color: #2e7d32;
        flex-grow: 1;
        text-align: left;
        margin-right: 10px;
        word-break: break-all;
        font-size: 0.9em;
    }

    .clear-btn, .remove-btn, .clear-all-btn {
        background: none;
        border: 1px solid var(--error-color);
        color: var(--error-color);
        padding: 5px 10px;
        border-radius: 5px;
        cursor: pointer;
        transition: background-color 0.2s;
        font-size: 0.85em;
        flex-shrink: 0;
        white-space: nowrap;
    }

    .clear-btn:hover, .remove-btn:hover, .clear-all-btn:hover {
        background-color: #ffebee;
    }

    .clear-all-btn {
        align-self: flex-end;
        margin-top: 10px;
    }


    .options-section {
        background-color: #fafafa;
        padding: 15px;
        border-radius: 10px;
        margin-bottom: 20px;
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        align-items: center;
        justify-content: flex-start;
    }

    .options-section label {
        display: flex;
        flex-direction: column;
        font-weight: 500;
        color: var(--dark-text);
        margin-right: 10px;
    }

    .options-section input[type='range'] {
        width: 100px;
        margin-top: 5px;
    }

    .options-section select {
        padding: 5px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background-color: white;
        margin-top: 5px;
        cursor: pointer;
    }

    .options-section span {
        margin-left: 5px;
        font-size: 0.9em;
        color: var(--light-text);
        white-space: nowrap;
    }


    .action-section {
        text-align: center;
        margin-bottom: 20px;
    }

    /* --- 修正后的按钮样式 --- */
    /* 基础按钮类 */
    .btn {
        font-size: 1.1em;
        padding: 12px 30px;
        border-radius: 50px;
        border: none;
        color: white;
        cursor: pointer;
        transition: all 0.2s ease;
        width: 100%;
        max-width: 300px;
        box-sizing: border-box;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
        text-decoration: none; /* 针对 <a> 标签 */
        display: inline-block; /* 针对 <a> 标签 */
        text-align: center; /* 针对 <a> 标签 */
        font-weight: 500;
    }

    /* 启用状态的按钮样式 */
    .btn-enabled {
        background: var(--button-primary-gradient); /* 启用时的蓝色渐变 */
    }

    /* 启用状态悬停 */
    .btn-enabled:hover {
        background: var(--button-primary-gradient-hover);
        transform: translateY(-2px);
        box-shadow: 0 5px 15px var(--button-hover-shadow);
    }

    /* 禁用状态的按钮样式 - 关键修改在这里 */
    .btn-disabled {
        background: var(--button-disabled-bg) !important; /* 明确指定禁用时的灰色背景 */
        color: var(--button-disabled-text) !important;    /* 明确指定禁用时的文字颜色 */
        cursor: not-allowed !important;                   /* 明确指定禁用时的光标 */
        opacity: 1 !important;                            /* 确保不透明度是1，不是半透明 */
        transform: none !important;                       /* 确保没有位移 */
        box-shadow: none !important;                      /* 确保没有阴影 */
        pointer-events: none;                             /* 阻止所有鼠标事件 */
        /* 移除了所有可能的过渡效果，确保样式立即生效 */
        transition: none !important;
    }

    /* 下载链接样式 (保持不变) */
    .download-link {
        background: var(--button-secondary-bg);
        color: white;
        padding: 8px 15px;
        border-radius: 5px;
        text-decoration: none;
        display: inline-block;
        margin-top: 5px;
        transition: background-color 0.2s ease;
        font-size: 0.9em;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }
    .download-link:hover {
        background: var(--button-secondary-bg-hover);
        transform: translateY(-1px);
        box-shadow: 0 3px 8px rgba(76, 175, 80, 0.3);
    }


    .status-message {
        margin: 15px auto;
        padding: 12px 15px;
        border-radius: 8px;
        font-weight: 500;
        text-align: center;
        max-width: 90%;
        word-break: break-word;
    }

    .status-message:not(.error) {
        background-color: #e3f2fd;
        color: #1976d2;
        border: 1px solid #90caf9;
    }

    .status-message.error {
        background-color: #ffebee;
        color: #c62828;
        border: 1px solid #ffcdd2;
    }

    .results-section h3 {
        margin-top: 20px;
        margin-bottom: 15px;
        color: var(--dark-text);
        text-align: center;
    }

    .image-preview-container {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        justify-content: center;
        margin-bottom: 20px;
    }

    .image-preview-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1 1 calc(33.333% - 20px); /* Default 3 columns */
        min-width: 150px; /* Minimum width before wrapping */
        max-width: calc(50% - 10px); /* Max 2 columns on smaller screens */
    }

    .preview-image {
        max-width: 100%;
        height: auto;
        border: 1px solid #ddd;
        border-radius: 5px;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    }

    .image-preview-item p {
        margin: 5px 0;
        font-size: 0.9em;
        color: var(--light-text);
    }


    /* Responsive adjustments */
    @media (max-width: 768px) {
        .converter-app {
            padding: 10px;
        }
        .tab-content {
            padding: 20px;
        }
        .tabs button {
            padding: 12px 15px;
            font-size: 0.9em;
        }
        .options-section {
            flex-direction: column;
            align-items: flex-start;
        }
        .image-preview-item {
            flex: 1 1 calc(50% - 10px); /* 2 columns on mobile */
            max-width: 100%; /* Override max-width for mobile */
        }
        .file-upload-label {
            padding: 20px 10px;
        }
        .upload-text {
            font-size: 1em;
        }
        .selected-file, .selected-files {
            flex-direction: column;
            align-items: flex-start;
        }
        .file-name {
            margin-bottom: 10px;
            margin-right: 0;
        }
        .clear-btn, .clear-all-btn {
            align-self: flex-end;
        }
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 800px;
        margin: 40px auto;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #2196f3;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
    .tool-content code {
        background: #e9ecef;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: monospace;
        color: #d63384;
    }
</style>



